import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { loginSuccess } from '../slices/authSlice';
import { GoogleLogin } from '@react-oauth/google';
import {
    CheckCircleIcon,
    ExclamationCircleIcon,
    EyeIcon,
    EyeSlashIcon
} from '@heroicons/react/24/outline';
import { trackUserRegistration, trackError } from '../utils/analytics';

const Register = () => {
    const [searchParams] = useSearchParams();
    const referralCode = searchParams.get('ref');
    const afterAuthPath = searchParams.get('redirect') === '/subscription' ? '/subscription' : '/upload';
    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleGoogleSuccess = async (credentialResponse) => {
        try {
            const res = await fetch('https://cvanalyzerback.osc-fr1.scalingo.io/api/users/google-auth', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ credential: credentialResponse.credential }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message || 'Erreur Google');
            trackUserRegistration(data.user);
            dispatch(loginSuccess({ token: data.token, refreshToken: data.refreshToken, user: data.user }));
            navigate(afterAuthPath);
        } catch (err) {
            console.error('Google auth error:', err);
        }
    };
    const [isSuccess, setIsSuccess] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [emailError, setEmailError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const { username, email, password } = formData;

    const regexEmail = /^[^@\s]{2,30}@[^@\s]{2,30}\.[^@\s]{2,5}$/;
    const regexPassword = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9]).{8,}$/;

    const validateEmail = (email) => {
        if (!regexEmail.test(email)) {
            setEmailError("L'adresse email n'est pas valide.");
        } else {
            setEmailError('');
        }
    };

    const validatePassword = (password) => {
        if (!regexPassword.test(password)) {
            setPasswordError('Le mot de passe doit comporter au moins 8 caractères, une majuscule et un chiffre.');
        } else {
            setPasswordError('');
        }
    };

    const passwordChecks = [
        { label: '8 caractères minimum', ok: password.length >= 8 },
        { label: 'Une majuscule', ok: /[A-Z]/.test(password) },
        { label: 'Un chiffre', ok: /[0-9]/.test(password) },
    ];

    const onChange = e => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        // Supprimer les erreurs quand l'utilisateur tape
        if (e.target.name === 'email' && emailError) {
            setEmailError('');
        }
        if (e.target.name === 'password' && passwordError) {
            setPasswordError('');
        }
    };

    const onBlur = e => {
        // Valider seulement quand l'utilisateur quitte le champ
        if (e.target.name === 'email' && e.target.value) {
            validateEmail(e.target.value);
        }
        if (e.target.name === 'password' && e.target.value) {
            validatePassword(e.target.value);
        }
    };

    const onSubmit = async e => {
        e.preventDefault();
        if (emailError || passwordError) {
            return;
        }

        setIsSubmitting(true);
        setIsSuccess(null);
        setErrorMessage('');
        try {
            const response = await fetch('https://cvanalyzerback.osc-fr1.scalingo.io/api/users/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ 
                    username, 
                    email, 
                    password,
                    referralCode: referralCode || null
                })
            });
            const data = await response.json();
            if (response.ok) {
                trackUserRegistration(data.user);
                // Auto-login : le backend retourne directement un token
                dispatch(loginSuccess({
                    token: data.token,
                    refreshToken: data.refreshToken,
                    user: data.user,
                }));
                navigate(afterAuthPath);
                return;
            } else {
                setIsSuccess(false);
                // Gérer les messages d'erreur du backend (message ou msg)
                const errorMsg = data.message || data.msg || 'Une erreur est survenue, veuillez réessayer.';
                setErrorMessage(errorMsg);
                trackError(errorMsg, 'register');
            }
        } catch (error) {
            console.error('Erreur lors de l\'enregistrement de l\'utilisateur:', error);
            setIsSuccess(false);
            setErrorMessage('Erreur de réseau, veuillez réessayer.');
            trackError('Erreur réseau', 'register');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-100 pt-16 flex items-center justify-center p-4">
            <Helmet>
                <title>Inscription | CV Analyse</title>
                <meta name="robots" content="index, follow" />
                <link rel="canonical" href="https://www.cv-analyse.fr/register" />
            </Helmet>
            {isSuccess === true ? (
                <div className="card-surface p-8 max-w-md w-full text-center">
                    <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircleIcon className="w-8 h-8 text-green-600" />
                    </div>
                    <h2 className="text-2xl font-semibold mb-4 text-gray-900">Inscription réussie</h2>
                    <p className="text-gray-600 mb-6">
                        Votre compte a été créé. Vous pouvez vous connecter et analyser votre CV.
                    </p>
                    <p className="text-sm text-gray-500 mb-6">
                        {referralCode ? 'Aperçu offert + bonus parrainage sur votre compte.' : 'Score et 3 conseils offerts sur votre compte.'}
                    </p>
                    <Link
                        to="/login"
                        className="btn-primary"
                    >
                        Se connecter
                    </Link>
                </div>
            ) : (
                <div className="card-surface p-6 sm:p-8 max-w-md w-full">
                    <div className="mb-6">
                        <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">
                            Créer un compte
                        </h1>
                        <p className="text-slate-600 text-sm leading-relaxed mt-2">
                            Score et 3 conseils offerts, sans carte bancaire. Rapport complet dès 5 €.
                        </p>
                        {referralCode && (
                            <p className="text-sm text-slate-700 mt-3">
                                Parrainage : aperçu offert + 1 crédit bonus
                            </p>
                        )}
                    </div>

                    <div className="mb-6">
                        <div className="flex justify-center">
                            <GoogleLogin
                                onSuccess={handleGoogleSuccess}
                                onError={() => {}}
                                text="signup_with"
                                shape="rectangular"
                                size="large"
                                width="100%"
                                locale="fr"
                            />
                        </div>
                        <div className="flex items-center my-5">
                            <div className="flex-1 border-t border-slate-200" />
                            <span className="px-3 text-sm text-slate-400">ou</span>
                            <div className="flex-1 border-t border-slate-200" />
                        </div>
                    </div>

                    <form onSubmit={onSubmit} className="space-y-4">
                        <div>
                            <label className="field-label" htmlFor="reg-username">
                                Nom d&apos;utilisateur
                            </label>
                            <input
                                id="reg-username"
                                type="text"
                                name="username"
                                value={username}
                                onChange={onChange}
                                required
                                autoComplete="username"
                                className="field-input"
                                placeholder="Votre nom d'utilisateur"
                            />
                        </div>

                        <div>
                            <label className="field-label" htmlFor="reg-email">
                                Adresse email
                            </label>
                            <input
                                id="reg-email"
                                type="email"
                                name="email"
                                value={email}
                                onChange={onChange}
                                onBlur={onBlur}
                                required
                                autoComplete="email"
                                aria-invalid={Boolean(emailError)}
                                aria-describedby={emailError ? 'reg-email-error' : undefined}
                                className={`field-input ${emailError ? 'border-red-500' : ''}`}
                                placeholder="exemple@email.com"
                            />
                            {emailError && (
                                <div id="reg-email-error" className="mt-2 flex items-center text-red-600 text-sm">
                                    <ExclamationCircleIcon className="w-4 h-4 mr-2 flex-shrink-0" />
                                    <span>{emailError}</span>
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="field-label" htmlFor="reg-password">
                                Mot de passe
                            </label>
                            <div className="relative">
                                <input
                                    id="reg-password"
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    value={password}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    required
                                    autoComplete="new-password"
                                    aria-invalid={Boolean(passwordError)}
                                    aria-describedby={passwordError ? 'reg-password-error' : undefined}
                                    className={`field-input pr-11 ${passwordError ? 'border-red-500' : ''}`}
                                    placeholder="8 caractères, une majuscule, un chiffre"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                                    aria-pressed={showPassword}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                >
                                    {showPassword ? (
                                        <EyeSlashIcon className="w-5 h-5" />
                                    ) : (
                                        <EyeIcon className="w-5 h-5" />
                                    )}
                                </button>
                            </div>
                            {password.length > 0 && (
                                <div className="mt-2 flex gap-3">
                                    {passwordChecks.map(({ label, ok }) => (
                                        <span key={label} className={`text-xs flex items-center gap-1 ${ok ? 'text-green-600' : 'text-gray-400'}`}>
                                            {ok ? '✓' : '○'} {label}
                                        </span>
                                    ))}
                                </div>
                            )}
                            {passwordError && !password.length && (
                                <div id="reg-password-error" className="mt-2 flex items-start text-red-500 text-sm">
                                    <ExclamationCircleIcon className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" />
                                    <span>{passwordError}</span>
                                </div>
                            )}
                        </div>

                        {/* Bouton de soumission */}
                        <button
                            type="submit"
                            disabled={isSubmitting || emailError || passwordError}
                            className={`w-full ${
                                isSubmitting || emailError || passwordError
                                    ? 'bg-slate-300 text-white cursor-not-allowed py-2.5 px-5 rounded-lg font-medium flex items-center justify-center'
                                    : 'btn-primary'
                            }`}
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Création en cours...
                                </>
                            ) : (
                                'Créer mon compte'
                            )}
                        </button>

                        {errorMessage && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center">
                                <ExclamationCircleIcon className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" />
                                <span className="text-red-600 text-sm">{errorMessage}</span>
                            </div>
                        )}

                        <div className="text-center pt-4 border-t border-slate-200">
                            <p className="text-slate-600 text-sm">
                                Déjà un compte ?{' '}
                                <Link
                                    to={searchParams.get('redirect') === '/subscription' ? '/login?redirect=/subscription' : '/login'}
                                    className="text-blue-600 hover:text-blue-700 font-medium"
                                >
                                    Se connecter
                                </Link>
                            </p>
                            <p className="text-slate-500 text-xs mt-3">
                                Besoin d’aide pour optimiser votre CV ?
                                <Link to="/guide/optimiser-cv-ats" className="ml-1 text-blue-600 hover:text-blue-700 font-medium">Voir le guide ATS</Link>
                            </p>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default Register;
